/**
 * REPLICATED PAGE LAYOUT — the typed section vocabulary behind the visual page
 * builder at Admin → Replicated Pages. A replicated page is the public landing
 * page a prospect sees when they open a member's referral link (`/r/<code>`,
 * `/r/<code>/<slug>`, `/c/<slug>`).
 *
 * Replaces the pre-builder model, where a page was a flat row-per-block table
 * whose `content` was `z.record(z.unknown())` — untyped, so the editor could
 * offer exactly one styling knob (`align`) and the renderer was locked to a
 * `max-w-2xl` column. Here a page is ONE versioned jsonb blob holding an ordered
 * discriminated union, mirroring the proven `StorefrontLayout` in
 * `storefront-sections.ts`.
 *
 * DISCIPLINE (inherited from storefront-sections.ts — keep this bounded):
 *  - Section TYPES are a closed core registry. A new reusable look is a new core
 *    type reviewed like code — never a per-tenant one-off.
 *  - Every numeric/enum knob is a bounded union, CLAMPED ON READ. A stored value
 *    outside the union must degrade to a baseline, never throw: these pages are
 *    public, unauthenticated, and older than any given deploy.
 *  - NO Tailwind class strings in this file. Neither app's `tailwind.config.cjs`
 *    globs `packages/shared`, so a class written here is silently purged. Class
 *    maps live in `packages/ui/src/replicated/tokens.ts`, which IS globbed.
 *  - Images are POINTERS (R2 object keys served by `/replicated-page-image?key=`),
 *    never inlined bytes or absolute origins.
 *
 * @see packages/ui/src/replicated/ — the single renderer, shared by the member
 *      app (live) and the admin app (preview), so the two can never drift.
 */

// Type-only import — the closed icon allow-list shared with the storefront CMS.
// (Value-importing would create a runtime cycle; same pattern as
// storefront-sections.ts.)
import type { StorefrontIconKey } from './storefront-content';

// ── Style tokens ────────────────────────────────────────────────────────────

/** Band background. `image` draws `bgImageKey` behind an `overlay` scrim. */
export type RpBg = 'page' | 'muted' | 'tint' | 'accent' | 'dark' | 'image';
/**
 * Inner content width. `full` is what lets a section escape the historic
 * `max-w-2xl` column and go edge-to-edge — the single most-requested thing the
 * old editor could not express at any setting.
 */
export type RpWidth = 'narrow' | 'normal' | 'wide' | 'full';
/** Vertical rhythm around the band. */
export type RpPad = 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type RpAlign = 'left' | 'center' | 'right';
/** Force text color against a busy background image; `auto` follows the band. */
export type RpTone = 'auto' | 'dark' | 'light';
/** Darkness of the scrim over `bgImageKey`, as a percentage. */
export type RpOverlay = 0 | 20 | 40 | 60 | 80;
export type RpRadius = 'none' | 'sm' | 'md' | 'lg' | 'xl';
export type RpCols = 1 | 2 | 3 | 4;

/** Per-section presentation. Every knob optional → falls through to a baseline. */
export type RpSectionStyle = {
  bg?: RpBg;
  /** R2 object key. Only meaningful with `bg: 'image'`. */
  bgImageKey?: string;
  overlay?: RpOverlay;
  width?: RpWidth;
  padY?: RpPad;
  align?: RpAlign;
  textTone?: RpTone;
  radius?: RpRadius;
};

// ── Links ───────────────────────────────────────────────────────────────────

/**
 * Schemes a stored URL may use. Deliberately excludes `javascript:` and `data:`.
 *
 * This regex is the fix for a real defect in the pre-builder model: block content
 * was unvalidated, so a `button` block's raw `href` reached `<a href={…}>` on an
 * unauthenticated public page. Anyone who could edit a page could plant
 * `javascript:…` (React 18 warns but does not block). The typed union below
 * closes that by construction; this pattern is the write-time enforcement AND
 * the read-time clamp.
 */
export const SAFE_URL_RE = /^(https?:\/\/|mailto:|tel:|\/)/i;

/**
 * Where a button goes. A typed union rather than a free string so that (a) an
 * unsafe scheme is unrepresentable, and (b) `signup` can never be authored in a
 * way that drops the sponsor's referral code — the href is DERIVED, not stored.
 */
export type RpLinkTarget =
  | { kind: 'none' }
  /** Signup, carrying the sponsor ref + campaign slug. */
  | { kind: 'signup' }
  /** Smooth-scroll to another section on this page (`#<sectionId>`). */
  | { kind: 'anchor'; sectionId: string }
  /** An external or site-relative URL. Constrained to SAFE_URL_RE. */
  | { kind: 'url'; url: string }
  /** Signup with an activation kit preselected. */
  | { kind: 'kit'; productId: string }
  /** Buy a specific product — straight to checkout, registering on the way if
   *  the visitor is new. Used to sell a service/course from its campaign page. */
  | { kind: 'product'; productId: string }
  /** Another replicated page, ref preserved. */
  | { kind: 'page'; slug: string };

export type RpButtonVariant = 'primary' | 'outline' | 'ghost' | 'light';
export type RpButtonSize = 'sm' | 'md' | 'lg';

export type RpButton = {
  label: string;
  link: RpLinkTarget;
  variant?: RpButtonVariant;
  size?: RpButtonSize;
};

/** What a page needs to resolve its links: the visitor's sponsor + campaign. */
export type RpLinkCtx = { ref?: string; campaignSlug?: string };

/**
 * Resolve a typed link to an href. The ONE place a replicated-page URL is built,
 * so the admin preview and the live page can never disagree, and so sponsor
 * attribution can never be edited away.
 *
 * Mirrors the historic derivation the member page has always used: `ref`
 * preserves sponsor attribution, `c` lets a tagged campaign narrow the lead's
 * buyable kits (the server intersects the campaign's package tags with the
 * sponsor tier's).
 *
 * Defensive on READ — an unknown kind or an unsafe stored URL yields `'#'`
 * rather than throwing, because a page authored under an older schema must
 * still render.
 */
export function resolveRpLink(target: RpLinkTarget | undefined, ctx: RpLinkCtx): string {
  const signup = (extra?: Record<string, string>) => {
    const p = new URLSearchParams();
    if (ctx.ref) p.set('ref', ctx.ref);
    if (ctx.campaignSlug) p.set('c', ctx.campaignSlug);
    for (const [k, v] of Object.entries(extra ?? {})) p.set(k, v);
    const qs = p.toString();
    return `/signup${qs ? `?${qs}` : ''}`;
  };
  switch (target?.kind) {
    case 'signup':
      return signup();
    case 'kit':
      // `?pkg=` is the EXISTING per-package signup link: the server validates it
      // as an active kit and stores it on the new user so their dashboard scopes
      // the kit offering to that package. Reusing it means a package button on a
      // landing page behaves exactly like a package link shared any other way —
      // no parallel mechanism, no server change.
      return signup({ pkg: target.productId });
    case 'product': {
      // `/buy/:productId` handles BOTH visitors: signed in → the product goes
      // in the cart and they land on checkout; signed out → it redirects to
      // signup carrying `?buy=`, and signup returns here afterwards. Pointing
      // straight at `/signup?buy=` instead would shove a signup form at a
      // member who is already logged in.
      const p = new URLSearchParams();
      if (ctx.ref) p.set('ref', ctx.ref);
      if (ctx.campaignSlug) p.set('c', ctx.campaignSlug);
      const qs = p.toString();
      return `/buy/${encodeURIComponent(target.productId)}${qs ? `?${qs}` : ''}`;
    }
    case 'anchor':
      return `#${target.sectionId}`;
    case 'page':
      return ctx.ref
        ? `/r/${encodeURIComponent(ctx.ref)}/${encodeURIComponent(target.slug)}`
        : `/c/${encodeURIComponent(target.slug)}`;
    case 'url':
      return SAFE_URL_RE.test(target.url) ? target.url : '#';
    default:
      return '#';
  }
}

/** True when a resolved href leaves the app (so the anchor needs rel/target). */
export function isRpExternalLink(target: RpLinkTarget | undefined): boolean {
  return target?.kind === 'url' && /^https?:\/\//i.test(target.url);
}

/**
 * Where the AUTO-APPENDED page CTA goes (the button the member SPA adds below
 * the content when `cta_auto` is on). Feed the result to `resolveRpLink`.
 *
 * Precedence: URL `?buy=` > the page's own product > sign-up.
 *
 * `?buy=` wins because it names the product the visitor ACTUALLY clicked: the
 * product→page link is many-to-one (`products.replicated_page_id`), so one page
 * can front several products and only the URL knows which one sent them.
 *
 * The page's product is what a SHARED `/r/<code>/<slug>` link has instead —
 * there was no click to carry the intent. Without it that link's CTA could only
 * ever offer sign-up, and a visitor who came to BUY registered and landed on the
 * dashboard with no trace of what they came for.
 *
 * Neither set → `signup`, byte-identical to the behaviour before this existed.
 *
 * NOTE this deliberately does NOT touch a stored `{kind:'signup'}` button. A
 * link means what it was authored to mean; the page's product is a statement
 * about the automatic button only. The admin builder flags the mismatch instead.
 */
export function resolveRpAutoCtaLink(args: {
  /** `?buy=` on the page URL — a storefront click on a virtual product. */
  buyProductId?: string | null;
  /** `replicated_pages.cta_product_id` — what this page says it sells. */
  pageCtaProductId?: string | null;
}): RpLinkTarget {
  const productId = args.buyProductId?.trim() || args.pageCtaProductId?.trim() || '';
  return productId ? { kind: 'product', productId } : { kind: 'signup' };
}

// ── Media ───────────────────────────────────────────────────────────────────

/**
 * An image or video reference.
 *
 * `legacyImage` exists solely so pages authored before the builder keep their
 * exact `<img src>`: those images live as rows in `replicated_page_blocks` and
 * are served by `GET /replicated-page/blocks/:id/image`. New images use the
 * key-based public route instead, so the builder needs no new image endpoint.
 */
export type RpMediaRef =
  | { kind: 'image'; imageKey: string; alt?: string }
  | { kind: 'legacyImage'; blockId: string; alt?: string }
  | { kind: 'video'; url: string };

/** An all-digits path segment — a Vimeo video / event id. */
const VIMEO_ID_RE = /^\d+$/;
/** A Vimeo unlisted-video privacy hash: 10 hex chars today, matched loosely. */
const VIMEO_HASH_RE = /^[0-9a-f]{6,}$/i;

/**
 * `?h=<hash>` for an unlisted video, from the query string or from the path
 * segment that follows the id. Hex-only on purpose: it rejects the non-id
 * segments that can also trail an id (`/settings`, `/embed`, `/video`).
 */
function vimeoHashQuery(u: URL, after: string | undefined): string {
  const h = u.searchParams.get('h') || (after && VIMEO_HASH_RE.test(after) ? after : '');
  return h ? `?h=${encodeURIComponent(h)}` : '';
}

/**
 * A vimeo.com / player.vimeo.com URL → the player embed URL.
 *
 * Two things this must get right. (1) The unlisted PRIVACY HASH: a "hide from
 * Vimeo" video 403s without `?h=`, with the same "because of its privacy
 * settings" message a domain lock produces. (2) The id is not always the first
 * path segment — Vimeo hides it behind /channels, /groups, /manage and
 * /showcase prefixes, and copy-from-share appends tracking params (`?fl=`,
 * `?fe=`) that must NOT survive. Returns null when there is no numeric id
 * (e.g. /ondemand), so the caller can fall back to a raw passthrough.
 */
function vimeoEmbedSrc(u: URL): string | null {
  const seg = u.pathname.split('/').filter(Boolean);

  // A livestream embeds through /event/<id>/embed, not /video/<id>.
  const first = seg[0];
  if (first === 'event' && seg[1]) {
    return `https://player.vimeo.com/event/${seg[1]}/embed${vimeoHashQuery(u, seg[2])}`;
  }

  // The id is the FIRST segment for the canonical /<id>[/<hash>] form, and the
  // LAST numeric one otherwise. Splitting the two cases matters: an all-digit
  // hash would otherwise win the reverse scan, while /showcase/<n>/video/<id>
  // has two numeric segments and needs the last.
  let id = '';
  let after: string | undefined;
  if (first && VIMEO_ID_RE.test(first)) {
    id = first;
    after = seg[1];
  } else {
    for (let k = seg.length - 1; k >= 0; k--) {
      const s = seg[k];
      if (s && VIMEO_ID_RE.test(s)) {
        id = s;
        after = seg[k + 1];
        break;
      }
    }
  }
  if (!id) return null;
  return `https://player.vimeo.com/video/${id}${vimeoHashQuery(u, after)}`;
}

/** YouTube path prefixes that carry the id in the NEXT segment. */
const YT_PATH_KINDS = ['shorts', 'live', 'embed', 'v'];

/**
 * The YouTube player URL, carrying a share timestamp through as `?start=`.
 * Only whole seconds — YouTube's `1m30s` duration form is dropped rather than
 * parsed.
 */
function ytEmbedSrc(id: string, u: URL): string {
  const t = (u.searchParams.get('start') ?? u.searchParams.get('t') ?? '').replace(/s$/, '');
  return `https://www.youtube.com/embed/${id}${/^\d+$/.test(t) ? `?start=${t}` : ''}`;
}

/**
 * Only `http(s)` may ever reach an `iframe`/`video` `src`. Everything else —
 * `javascript:`, `data:`, `vbscript:`, `file:` — executes in OUR origin when a
 * renderer passes it through, and every caller's URL is author-supplied
 * (creator lesson, admin training video, replicated-page media). Write-time
 * validation rejects them too; this is the read-side backstop for rows that
 * predate it.
 */
export function isEmbeddableUrl(url: string): boolean {
  try {
    const p = new URL(url).protocol;
    return p === 'https:' || p === 'http:';
  } catch {
    return false;
  }
}

/**
 * YouTube / Vimeo / direct-file URL → an embeddable source, or `null` when the
 * URL is unparseable or carries a non-http(s) scheme. Callers render nothing
 * for `null` — an unembeddable URL was never a working video.
 */
export function resolveRpEmbed(url: string): { kind: 'iframe' | 'video'; src: string } | null {
  if (!isEmbeddableUrl(url)) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, '');
    if (host === 'youtube.com' || host === 'm.youtube.com') {
      // /watch?v=<id>, plus the path forms: /shorts, /live, /embed, legacy /v.
      // The watch and shorts pages send X-Frame-Options, so a passthrough
      // iframe renders YouTube's refusal page instead of the video.
      const seg = u.pathname.split('/').filter(Boolean);
      const first = seg[0];
      const id = first && YT_PATH_KINDS.includes(first) ? seg[1] : u.searchParams.get('v');
      if (id) return { kind: 'iframe', src: ytEmbedSrc(id, u) };
    }
    if (host === 'youtu.be') {
      const id = u.pathname.split('/').filter(Boolean)[0];
      if (id) return { kind: 'iframe', src: ytEmbedSrc(id, u) };
    }
    if (host === 'vimeo.com' || host === 'player.vimeo.com') {
      const src = vimeoEmbedSrc(u);
      if (src) return { kind: 'iframe', src };
    }
    if (/\.(mp4|webm|ogg)(\?|$)/i.test(u.pathname)) return { kind: 'video', src: url };
  } catch {
    /* unreachable — isEmbeddableUrl already parsed it */
  }
  return { kind: 'iframe', src: url };
}

// ── List caps ───────────────────────────────────────────────────────────────

/**
 * Maximum rows per list-bearing config. Shared by the Zod write guard, the
 * admin editor's add-row control, and the renderer's `.slice()` — one source of
 * truth so the three can't disagree about what "too many" means.
 */
export const RP_LIST_CAPS = {
  heroBullets: 6,
  mediaTextBullets: 8,
  featureGridItems: 12,
  stepsItems: 8,
  testimonialItems: 9,
  faqItems: 20,
  statsItems: 6,
  logoStripItems: 12,
  galleryItems: 16,
  buttonGroupButtons: 3,
} as const;

/** Hard cap on sections per page — mirrors the old 100-block cap. */
export const RP_MAX_SECTIONS = 100;

// ── The section union ───────────────────────────────────────────────────────

type RpBase<T extends string, C> = {
  type: T;
  /** Stable id — React key, `#anchor` target, and click-to-select handle. */
  id: string;
  enabled: boolean;
  config: C;
  style?: RpSectionStyle;
};

export type RpSection =
  // ── Convert: the sections that do the landing page's actual job ───────────
  | RpBase<
      'hero',
      {
        variant: 'imageBg' | 'split' | 'plain';
        eyebrow?: string;
        heading: string;
        subheading?: string;
        bullets?: string[];
        /** `split` only — the image/video beside the copy. */
        media?: RpMediaRef;
        primaryCta?: RpButton;
        secondaryCta?: RpButton;
        minHeight?: 'sm' | 'md' | 'lg';
      }
    >
  | RpBase<
      'cta',
      {
        eyebrow?: string;
        heading: string;
        subheading?: string;
        primaryCta: RpButton;
        secondaryCta?: RpButton;
      }
    >
  /** Kit cards. POINTS AT DATA: the page's own `kit_product_ids` tagging. */
  | RpBase<
      'packages',
      {
        heading?: string;
        subheading?: string;
        cols?: 1 | 2 | 3;
        source: 'campaignKits';
        showPrice?: boolean;
        showPv?: boolean;
        /** Per-card button wording; the link is always the kit's signup. */
        ctaLabel?: string;
      }
    >
  | RpBase<'countdown', { label: string; hours: number; variant: 'bar' | 'blocks' }>

  // ── Persuade ─────────────────────────────────────────────────────────────
  | RpBase<
      'mediaText',
      {
        variant: 'mediaLeft' | 'mediaRight';
        media: RpMediaRef;
        eyebrow?: string;
        heading?: string;
        /** Plain paragraphs, split on blank lines at render. */
        body?: string;
        bullets?: string[];
        cta?: RpButton;
      }
    >
  | RpBase<
      'featureGrid',
      {
        heading?: string;
        subheading?: string;
        cols?: RpCols;
        items: { icon?: StorefrontIconKey; imageKey?: string; title: string; body?: string }[];
        cardStyle?: 'plain' | 'card' | 'bordered';
      }
    >
  | RpBase<
      'steps',
      {
        heading?: string;
        subheading?: string;
        /** Numerals are derived from index — never authored. */
        items: { title: string; body?: string }[];
        layout?: 'row' | 'stack';
      }
    >
  /**
   * INLINE testimonials, deliberately not sourced from the storefront CMS: a
   * replicated page is campaign-scoped, and silently sharing quotes with an
   * independently-edited surface would surprise both editors.
   */
  | RpBase<
      'testimonials',
      {
        heading?: string;
        subheading?: string;
        cols?: 1 | 2 | 3;
        items: {
          quote: string;
          name: string;
          role?: string;
          avatarKey?: string;
          rating?: 1 | 2 | 3 | 4 | 5;
        }[];
      }
    >
  | RpBase<'faq', { heading?: string; subheading?: string; items: { q: string; a: string }[] }>
  | RpBase<
      'stats',
      { heading?: string; items: { value: string; label: string; icon?: StorefrontIconKey }[] }
    >
  | RpBase<
      'logoStrip',
      { heading?: string; grayscale?: boolean; items: { imageKey: string; alt?: string }[] }
    >
  | RpBase<
      'gallery',
      { heading?: string; cols?: 2 | 3 | 4; items: { imageKey: string; caption?: string }[] }
    >

  // ── Basic primitives — the five legacy block types land here ──────────────
  | RpBase<'heading', { text: string; level: 1 | 2 | 3; eyebrow?: string; subheading?: string }>
  /** `html` is the rich-text path; `text` is the pre-WYSIWYG plain-text path. */
  | RpBase<'richText', { html?: string; text?: string }>
  | RpBase<
      'image',
      {
        media: RpMediaRef;
        caption?: string;
        maxWidth?: 'sm' | 'md' | 'lg' | 'full';
        link?: RpLinkTarget;
      }
    >
  | RpBase<'video', { url: string; caption?: string; aspect?: '16:9' | '9:16' | '4:3' }>
  | RpBase<'buttonGroup', { buttons: RpButton[]; layout?: 'row' | 'stack' }>
  | RpBase<'spacer', { height: 'sm' | 'md' | 'lg' | 'xl'; divider?: boolean }>;

export type RpSectionType = RpSection['type'];

/** Narrow a section to one variant of the union (for per-type renderers). */
export type RpSectionOf<T extends RpSectionType> = Extract<RpSection, { type: T }>;

// ── Page ────────────────────────────────────────────────────────────────────

/**
 * Per-page theme overrides. Applied as inline CSS custom properties on the
 * page's root wrapper — NEVER on `<html>` — so a page can be reskinned without
 * touching the tenant's baked theme or any other route in the member SPA.
 * Colors are HSL triplets, matching the `ThemeTokens` convention in `theme.ts`.
 * An omitted token falls through to the tenant theme untouched.
 */
export type RpPageTheme = {
  accent?: string;
  accentFg?: string;
  pageBg?: string;
  pageFg?: string;
  cardBg?: string;
  mutedBg?: string;
  radius?: RpRadius;
  fontHeading?: string;
  fontBody?: string;
  /** Stylesheet URLs injected by the renderer host (e.g. Google Fonts). */
  fontLinks?: string[];
};

/**
 * A replicated page's content.
 *
 * `version` is load-bearing, not decorative:
 *  - **0** — converted on read from pre-builder `replicated_page_blocks` rows.
 *    The renderer wraps these in the HISTORIC `max-w-2xl` shell so an untouched
 *    legacy page stays byte-identical.
 *  - **1** — authored in the builder. Sections own their own full-width bands.
 */
export type RpLayout = {
  version: 0 | 1;
  theme?: RpPageTheme;
  sections: RpSection[];
};

/** An empty builder-authored page. */
export function emptyRpLayout(): RpLayout {
  return { version: 1, sections: [] };
}

// ── Section library — the admin "Add section" palette ────────────────────────

/** Palette grouping, so the picker is three short tabs and not an 18-item list. */
export type RpSectionGroup = 'convert' | 'persuade' | 'basic';

export type RpSectionLibraryEntry = {
  [T in RpSectionType]: {
    type: T;
    /** Human label shown in the Add-section picker. */
    label: string;
    /** One-line hint of what the section is for / where its content comes from. */
    note: string;
    group: RpSectionGroup;
    /** A default `config` that satisfies the write-time schema. */
    defaultConfig: RpSectionOf<T>['config'];
    /** Presentation the section looks best with out of the box. */
    defaultStyle?: RpSectionStyle;
  };
}[RpSectionType];

/**
 * Every placeable section type with a label and a schema-valid default config.
 * MIRRORS the `RpSection` union above — add an entry here when you add a type,
 * or `createRpSection` will throw for it and the palette won't offer it.
 */
export const RP_SECTION_LIBRARY: RpSectionLibraryEntry[] = [
  // ── convert ──
  {
    type: 'hero',
    label: 'Hero',
    note: 'Big opening band — headline, sub-copy and the main call to action.',
    group: 'convert',
    defaultConfig: {
      variant: 'imageBg',
      heading: 'Your headline goes here',
      subheading: 'One sentence on why this is worth their time.',
      primaryCta: {
        label: 'Get started',
        link: { kind: 'signup' },
        variant: 'primary',
        size: 'lg',
      },
      minHeight: 'md',
    },
    defaultStyle: { bg: 'dark', width: 'wide', padY: 'xl', align: 'center', overlay: 40 },
  },
  {
    type: 'cta',
    label: 'Call to action',
    note: 'A focused conversion band. Put one near the bottom of every page.',
    group: 'convert',
    defaultConfig: {
      heading: 'Ready to start?',
      primaryCta: { label: 'Sign up', link: { kind: 'signup' }, variant: 'primary', size: 'lg' },
    },
    defaultStyle: { bg: 'accent', width: 'normal', padY: 'lg', align: 'center' },
  },
  {
    type: 'packages',
    label: 'Packages',
    note: "Cards for the activation kits tagged on this page's Packages tab.",
    group: 'convert',
    defaultConfig: {
      source: 'campaignKits',
      heading: 'Choose your package',
      cols: 3,
      showPrice: true,
    },
    defaultStyle: { bg: 'muted', width: 'wide', padY: 'lg' },
  },
  {
    type: 'countdown',
    label: 'Countdown',
    note: 'Rolling urgency timer. Each visitor starts at the full duration.',
    group: 'convert',
    defaultConfig: { label: 'Offer ends in', hours: 24, variant: 'bar' },
    defaultStyle: { bg: 'tint', width: 'normal', padY: 'sm', align: 'center' },
  },
  // ── persuade ──
  {
    type: 'mediaText',
    label: 'Image + text',
    note: 'A picture or video beside a block of copy.',
    group: 'persuade',
    defaultConfig: {
      variant: 'mediaRight',
      media: { kind: 'image', imageKey: '' },
      heading: 'Section heading',
      body: 'Explain one idea here.',
    },
    defaultStyle: { bg: 'page', width: 'wide', padY: 'lg' },
  },
  {
    type: 'featureGrid',
    label: 'Benefits grid',
    note: 'Icon or image cards — the "why join" list.',
    group: 'persuade',
    defaultConfig: { heading: 'Why join', cols: 3, items: [], cardStyle: 'card' },
    defaultStyle: { bg: 'muted', width: 'wide', padY: 'lg', align: 'center' },
  },
  {
    type: 'steps',
    label: 'How it works',
    note: 'Numbered steps. Numerals come from the row order.',
    group: 'persuade',
    defaultConfig: { heading: 'How it works', items: [], layout: 'row' },
    defaultStyle: { bg: 'page', width: 'wide', padY: 'lg', align: 'center' },
  },
  {
    type: 'testimonials',
    label: 'Testimonials',
    note: 'Quote cards. Written here, not shared with the storefront.',
    group: 'persuade',
    defaultConfig: { heading: 'What members say', cols: 3, items: [] },
    defaultStyle: { bg: 'muted', width: 'wide', padY: 'lg', align: 'center' },
  },
  {
    type: 'faq',
    label: 'FAQ',
    note: 'Expandable question list — handles objections before the CTA.',
    group: 'persuade',
    defaultConfig: { heading: 'Frequently asked questions', items: [] },
    defaultStyle: { bg: 'page', width: 'normal', padY: 'lg' },
  },
  {
    type: 'stats',
    label: 'Stats',
    note: 'A row of big numbers — members, years, payouts.',
    group: 'persuade',
    defaultConfig: { items: [] },
    defaultStyle: { bg: 'tint', width: 'wide', padY: 'md', align: 'center' },
  },
  {
    type: 'logoStrip',
    label: 'Logo strip',
    note: 'Partner, press or accreditation logos.',
    group: 'persuade',
    defaultConfig: { items: [], grayscale: true },
    defaultStyle: { bg: 'page', width: 'wide', padY: 'md', align: 'center' },
  },
  {
    type: 'gallery',
    label: 'Gallery',
    note: 'A grid of photos — events, products, the team.',
    group: 'persuade',
    defaultConfig: { cols: 3, items: [] },
    defaultStyle: { bg: 'page', width: 'wide', padY: 'lg' },
  },
  // ── basic ──
  {
    type: 'heading',
    label: 'Heading',
    note: 'A standalone title.',
    group: 'basic',
    defaultConfig: { text: 'Heading', level: 2 },
    defaultStyle: { bg: 'page', width: 'normal', padY: 'md' },
  },
  {
    type: 'richText',
    label: 'Text',
    note: 'Formatted paragraphs, lists, links and inline images.',
    group: 'basic',
    defaultConfig: { html: '' },
    defaultStyle: { bg: 'page', width: 'normal', padY: 'md' },
  },
  {
    type: 'image',
    label: 'Image',
    note: 'A single picture, optionally clickable.',
    group: 'basic',
    defaultConfig: { media: { kind: 'image', imageKey: '' }, maxWidth: 'lg' },
    defaultStyle: { bg: 'page', width: 'normal', padY: 'md', align: 'center' },
  },
  {
    type: 'video',
    label: 'Video',
    note: 'YouTube, Vimeo or a direct video URL.',
    group: 'basic',
    defaultConfig: { url: '', aspect: '16:9' },
    defaultStyle: { bg: 'page', width: 'normal', padY: 'md' },
  },
  {
    type: 'buttonGroup',
    label: 'Buttons',
    note: 'One to three buttons. Any of them can be the signup CTA.',
    group: 'basic',
    defaultConfig: {
      buttons: [{ label: 'Sign up', link: { kind: 'signup' }, variant: 'primary', size: 'md' }],
      layout: 'row',
    },
    defaultStyle: { bg: 'page', width: 'normal', padY: 'md', align: 'center' },
  },
  {
    type: 'spacer',
    label: 'Spacer / divider',
    note: 'Breathing room, with an optional rule.',
    group: 'basic',
    defaultConfig: { height: 'md' },
    defaultStyle: { bg: 'page', width: 'normal', padY: 'none' },
  },
];

/** Look up a section type's library entry (label + default config). */
export function rpSectionLibraryEntry(type: RpSectionType): RpSectionLibraryEntry | undefined {
  return RP_SECTION_LIBRARY.find((e) => e.type === type);
}

/**
 * Pick a stable, collision-free section id: the type itself, else `type-2`,
 * `type-3`, … Ids double as `#anchor` targets, so they must stay readable.
 */
function uniqueSectionId(base: string, existing: readonly string[]): string {
  if (!existing.includes(base)) return base;
  let n = 2;
  while (existing.includes(`${base}-${n}`)) n += 1;
  return `${base}-${n}`;
}

/**
 * Build a fresh, schema-valid section of `type` with a unique id. Powers the
 * admin's Add-section control. Throws on an unknown type — a caller can only
 * reach one by passing a string that isn't in the library.
 */
export function createRpSection(
  type: RpSectionType,
  existingIds: readonly string[] = [],
): RpSection {
  const entry = rpSectionLibraryEntry(type);
  if (!entry) throw new Error(`unknown replicated section type: ${type}`);
  // The type ↔ defaultConfig correlation is lost through the array lookup, so
  // the assembled object is asserted back to the union — correct by
  // construction, since each entry pairs a type with its own default config.
  return {
    type: entry.type,
    id: uniqueSectionId(type, existingIds),
    enabled: true,
    config: clone(entry.defaultConfig),
    ...(entry.defaultStyle ? { style: clone(entry.defaultStyle) } : {}),
  } as RpSection;
}

function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

// ── Legacy conversion ───────────────────────────────────────────────────────

/** One pre-builder `replicated_page_blocks` row, as read from the DB. */
export type LegacyReplicatedBlock = {
  id: string;
  type: string;
  align: string;
  content: Record<string, unknown>;
};

function legacyAlign(a: string): RpAlign {
  return a === 'center' ? 'center' : a === 'right' ? 'right' : 'left';
}

/**
 * Convert pre-builder block rows into a `version: 0` layout.
 *
 * Total and deterministic — it never throws, because it runs on every read of
 * every page that hasn't been re-saved in the builder yet, including pages on
 * live boxes authored years earlier. An unrecognized block type is skipped.
 *
 * `version: 0` tells the renderer to use the HISTORIC `max-w-2xl` page shell, so
 * an untouched legacy page renders byte-identically. The admin editor stamps
 * `version: 1` (and pins each section to `width: 'narrow'`) on first save, so
 * even the first save is a visual no-op and the page can then be widened one
 * section at a time.
 */
export function legacyBlocksToSections(blocks: readonly LegacyReplicatedBlock[]): RpLayout {
  const sections: RpSection[] = [];
  const ids: string[] = [];
  const push = (s: RpSection) => {
    sections.push(s);
    ids.push(s.id);
  };
  const mintId = (base: string) => {
    const id = uniqueSectionId(base, ids);
    return id;
  };

  for (const b of blocks) {
    const align = legacyAlign(b.align);
    const style: RpSectionStyle = { align, width: 'narrow', padY: 'none' };
    switch (b.type) {
      case 'heading':
        push({
          type: 'heading',
          id: mintId('heading'),
          enabled: true,
          // Level 1: matches what the member page has ALWAYS rendered. (The old
          // admin preview used <h2> — that was the drift, not the live page.)
          config: { text: str(b.content.text), level: 1 },
          style,
        });
        break;
      case 'text': {
        const html = typeof b.content.html === 'string' ? b.content.html : undefined;
        push({
          type: 'richText',
          id: mintId('richText'),
          enabled: true,
          // A pre-WYSIWYG block keeps its plain text VERBATIM — it is not
          // upgraded to HTML on read, because that would change the rendered
          // markup of a page nobody touched. The editor still offers the
          // text→HTML upgrade on first edit, exactly as before.
          config: html !== undefined ? { html } : { text: str(b.content.text) },
          style,
        });
        break;
      }
      case 'image':
        push({
          type: 'image',
          id: mintId('image'),
          enabled: true,
          // Points back at the block row, so the served URL stays
          // `/replicated-page/blocks/<id>/image` — byte-identical.
          config: { media: { kind: 'legacyImage', blockId: b.id }, maxWidth: 'full' },
          style,
        });
        break;
      case 'video':
        push({
          type: 'video',
          id: mintId('video'),
          enabled: true,
          config: { url: str(b.content.url), aspect: '16:9' },
          style,
        });
        break;
      case 'button': {
        const href = str(b.content.href);
        push({
          type: 'buttonGroup',
          id: mintId('buttonGroup'),
          enabled: true,
          config: {
            buttons: [
              {
                label: str(b.content.label) || 'Learn more',
                // An unsafe or empty legacy href becomes a dead button rather
                // than a live `javascript:` sink.
                link: SAFE_URL_RE.test(href) ? { kind: 'url', url: href } : { kind: 'none' },
                variant: 'outline',
                size: 'md',
              },
            ],
            layout: 'row',
          },
          style,
        });
        break;
      }
      default:
        // Unknown legacy type — skip rather than fail the whole page.
        break;
    }
  }

  return { version: 0, sections };
}

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}
